import { AfterViewInit, Component, forwardRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseComponent } from '@ex/module/core/domain/base-component';
import { EssentialObjectService } from '@ex/module/core/services/essential-object.service';
import grapesjs from 'grapesjs';
import { BehaviorSubject, debounceTime, take } from 'rxjs';

@Component({
  selector: 'email-editor',
  templateUrl: './email-editor.component.html',
  styleUrl: './email-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmailEditorComponent),
      multi: true,
    },
  ],
})
export class EmailEditorComponent extends BaseComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private onChange = (_: any) => { };
  private onTouched = () => { };
  private disabled = false;

  private styleEl!: HTMLLinkElement;
  private editor: any;

  private value = '';

  @Input() language!: string;

  emailEditorId = `gjs-email-editor-${(Math.random() * 100000).toFixed(0)}`;
  toolbarId = `rte-top-toolbar-${(Math.random() * 100000).toFixed(0)}`;

  constructor(private renderer: Renderer2, private essentialObjectService: EssentialObjectService) {
    super()

    this.essentialObjectService.essentialObject.pipe(take(1)).subscribe((obj) => {
      this.language = obj.language;
    })
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initEdtor();
    }, 700);
  }

  rteToolbar!: HTMLElement

  initEdtor() {
    if (!document.getElementById('gjs-css')) {
      this.styleEl = this.renderer.createElement('link');
      this.styleEl.id = 'gjs-css';
      this.styleEl.rel = 'stylesheet';
      this.styleEl.href = '/assets/grapesjs/grapesjs.min.css';
      this.renderer.appendChild(document.head, this.styleEl);
    }

    this.editor = grapesjs.init({
      container: `#${this.emailEditorId}`,
      height: '600px',
      storageManager: false,
      fromElement: false,
      panels: { defaults: [] },
    });
    const rte = this.editor.RichTextEditor

    rte.remove('link')
    rte.remove('unlink')
    rte.remove('wrap')

    rte.add('color', {
      icon: `<input type="color" class="gjs-field" style="width: 1.75rem; height: 1.75rem; padding: 0; border: none; background: transparent; cursor: pointer;"/>`,
      event: 'input',
      result: (rte, action) => {
        const input = action.btn.firstChild as HTMLInputElement;
        if (input?.value) {
          rte.exec('foreColor', input.value);
        }
      },
      update: (rte, action) => {
        const input = action.btn.firstChild as HTMLInputElement;
        if (!input) return;

        const color = rte.doc.queryCommandValue('foreColor');
        if (color && color !== 'false') {
          input.value = color;
        }
      }
    });


    rte.add('fontSize', {
      icon: `<select class="gjs-field"><option>T</option>
      ${Array.from({ length: 18 }, (_, i) => `<option value="${i + 1}">${i + 1}</option>`).join('')}</select>`,
      event: 'change',
      result: (rte, action) => {
        const value = (action.btn.firstChild as HTMLSelectElement).value;
        rte.exec('fontSize', value);
      },
      update: (rte, action) => {
        const value = rte.doc.queryCommandValue('fontSize');
        if (value && value !== 'false') {
          (action.btn.firstChild as HTMLSelectElement).value = value;
        }
      }
    });

    if (this.language == 'AR') {
      rte.add('justifyRight', {
        icon: '<i class="fas fa-align-right"></i>',
        attributes: { title: 'Align Right' },
        result: rte => rte.exec('justifyRight')
      });
      rte.add('justifyCenter', {
        icon: '<i class="fas fa-align-center"></i>',
        attributes: { title: 'Align Center' },
        result: rte => rte.exec('justifyCenter')
      });


      rte.add('justifyLeft', {
        icon: '<i class="fas fa-align-left"></i>',
        attributes: { title: 'Align Left' },
        result: rte => rte.exec('justifyLeft')
      });
    }
    else {
      rte.add('justifyLeft', {
        icon: '<i class="fas fa-align-left"></i>',
        attributes: { title: 'Align Left' },
        result: rte => rte.exec('justifyLeft')
      });

      rte.add('justifyCenter', {
        icon: '<i class="fas fa-align-center"></i>',
        attributes: { title: 'Align Center' },
        result: rte => rte.exec('justifyCenter')
      });

      rte.add('justifyRight', {
        icon: '<i class="fas fa-align-right"></i>',
        attributes: { title: 'Align Right' },
        result: rte => rte.exec('justifyRight')
      });

    }


    rte.add('insertUnorderedList', {
      icon: '<i class="fas fa-list-ul"></i>',
      attributes: { title: 'Unordered List' },
      result: rte => rte.exec('insertUnorderedList')
    });

    rte.add('insertOrderedList', {
      icon: '<i class="fas fa-list-ol"></i>',
      attributes: { title: 'Ordered List' },
      result: rte => rte.exec('insertOrderedList')
    });


    // rte.add('insertLink', {
    //   icon: '<i class="fas fa-link"></i>',
    //   attributes: { title: 'Insert Link' },
    //   result: async r => {
    //     const data = await firstValueFrom(this.ZenCDKService.open(EditorControlInputComponent, { language: this.language, controlType: EmailControlType.Link }))
    //     if (!data) return;

    //     const { linkUrl, linkText } = data as any;
    //     console.log({ linkUrl, linkText });

    //     r.insertHTML(
    //       `<a href="${linkUrl}" target="_blank" style="color: inherit; text-decoration: underline;">
    //       ${linkText}
    //     </a>`
    //     );
    //   }
    // });

    // rte.add('insertImage', {
    //   icon: '<i class="fas fa-image"></i>',
    //   attributes: { title: 'Insert Image' },
    //   result: async r => {
    //     const data = await firstValueFrom(this.ZenCDKService.open(EditorControlInputComponent, { language: this.language, controlType: EmailControlType.Image }))
    //     if (!data) return;

    //     const { imageLink } = data as any;
    //     console.log({ imageLink });

    //     r.insertHTML(
    //       `<img src="${imageLink}" alt="" style="max-width:100%; display:block;" />`
    //     );
    //   }
    // });



    const rteToolbar = rte.getToolbarEl()
    displayToolbar();
    const topToolbarContainer = document.getElementById(this.toolbarId);

    if (rteToolbar && topToolbarContainer) {
      topToolbarContainer.appendChild(rteToolbar);
    }

    if (this.value) {
      this.editor.setComponents(this.value);
    }

    this.editor.on('update', () => {
      const html = this.editor.getHtml();
      const css = this.editor.getCss();
      const finalHtml = `<!doctype html><html><head><style>${css}</style></head><body>${html}</body></html>`;
      this.value = finalHtml;
      this.onChange(finalHtml);
      this.onTouched();
    })

    this.editor.on('component:selected', component => {
      component.set({
        draggable: false,
      });
      displayToolbar();
    });

    this.editor.on('update', () => {
      displayToolbar();
    });

    function displayToolbar() {
      rteToolbar.style.position = 'static';
      rteToolbar.style.display = 'flex';
      rteToolbar.querySelector('.gjs-rte-actionbar').style.display = 'flex';
    }
    this.addSubscription(
      this.value$.pipe(debounceTime(300)).subscribe((value) => {

        this.value = value || '';

        if (this.editor && value) {
          this.editor.setComponents(value);
        }
      })
    )
  }

  value$ = new BehaviorSubject<string>('');

  writeValue(value: string): void {
    this.value$.next(value)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.editor) {
      this.editor.setDragMode(this.disabled ? 'none' : 'absolute');
    }
  }

  ngOnDestroy(): void {
    if (this.styleEl) {
      this.renderer.removeChild(document.head, this.styleEl);
    }

    if (this.editor) {
      this.editor.destroy();
    }
  }


}
